import { useEffect, useState, useRef } from 'react';
import {
  Layout,
  List,
  Avatar,
  Button,
  Input,
  Modal,
  Typography,
  Divider,
  Upload,
  message,
  Popconfirm,
  Spin,
  notification,
} from 'antd';
import { emit, on, removeAllListeners } from '@/socket';
import { useSelector, useDispatch } from 'react-redux';
import moment from 'moment';
import { PaperClipOutlined, SendOutlined, FileOutlined, DeleteOutlined } from '@ant-design/icons';
import StartNewChatModal from '../startNewChatModal/StartNewChatModal';
import { setShowUnreadIndicator } from '@/shared/store/uiSlice';
import { resetUnreadMessages } from '@/shared/store/unreadMessagesSlice';
import { useLocation } from 'react-router-dom';
import styles from './ChatComponent.module.css';
import { api } from '@/shared/api/api';

const { Header, Sider, Content } = Layout;
const { TextArea } = Input;
const { Text } = Typography;

const ChatComponent = () => {
  const [messages, setMessages] = useState([]);
  const [chats, setChats] = useState([]);
  const [chatName, setChatName] = useState('');
  const [chatParticipants, setChatParticipants] = useState('');
  const [actualChat, setActualChat] = useState(null);
  const [selectedChatId, setSelectedChatId] = useState(null);
  const userInfo = useSelector(state => state.userInfo.userState);
  const unreadMessages = useSelector(state => state.unreadMessages);
  const messagesEndRef = useRef(null);
  const [textMessage, setTextMessage] = useState('');
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [participantModalVisible, setParticipantModalVisible] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [newMessage, setNewMessage] = useState(0);
  const [fullArrayMessages, setFullArrayMessages] = useState(false);
  const [isFileLoading, setIsFileLoading] = useState(false);
  const [subs, setSubs] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const dispatch = useDispatch();
  const location = useLocation();
  const chatFromUrl = location.search.split('?')[1];

  const [notificationApi, contextHolder] = notification.useNotification();

  const abortControllerRef = useRef(null);

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  useEffect(() => {
    if (chatFromUrl) {
      handleChatClick(chatFromUrl);
    }
  }, [chatFromUrl]);

  useEffect(() => {
    on('new_chat', () => {
      fetchActualChats();
    });

    on('message_v_0.4', async data => {
      if (data.status === 'new_message') {
        if (actualChat === data.chatID) {
          await fetchChatInfo(data.chatID, messages?.length + 1);
          dispatch(setShowUnreadIndicator(false));
        }
        setNewMessage(newMessage + 1);
      }
    });

    return function cleanup() {
      removeAllListeners('message_v_0.4');
      removeAllListeners('new_chat');
    };
  }, [actualChat, messages]);

  useEffect(() => {
    fetchActualChats();
    setNewMessage(newMessage + 1);
  }, [actualChat]);

  useEffect(() => {
    if (newMessage !== 0) {
      scrollToBottom();
    }
  }, [newMessage]);

  useEffect(() => {
    if (subs?.length > 0) {
      setParticipantModalVisible(true);
    }
  }, [subs]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchChatInfo = async (chat, limit) => {
    try {
      const { data } = await api.get(`/api/message/getChatMessages?chatID=${chat}&limit=${limit}`);
      if (actualChat !== chat) {
        setActualChat(chat);
      }
      setMessages(data.chatInfo.messages.reverse());
      setChatName(data.chatInfo.chatName);
      setChatParticipants(data.chatInfo.chatAndSubs);
      setFullArrayMessages(data.fullArrayMessages);
      dispatch(resetUnreadMessages({ chatId: chat }));
    } catch (error) {
      console.log(error);
    }
  };

  const fetchMoreMessages = async chat => {
    setNewMessage(0);
    await fetchChatInfo(chat, messages?.length + 30);
  };

  const fetchActualChats = async () => {
    try {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      const { data } = await api.get(`/api/message/getChats?userId=${userInfo.userId}`, {
        signal: abortController.signal,
      });
      setChats(data);
      if (data.length) {
        for (const i of data) {
          emit('join-room', `chat-${i.id}`);
        }
      }
    } catch (error) {
      console.log(error);
    }
  };

  const getSenderName = (message, chatSubs) => {
    if (`${message.senderID}` === `${userInfo.userId}`) {
      return 'Вы';
    }
    for (const item of chatSubs) {
      if (item.users && `${message.senderID}` === `${item.users[0].id}`) {
        return item.users[0].name || item.users[0].email;
      }
    }
    return 'Пользователь ' + message.senderID;
  };

  const fetchSubs = async chat => {
    try {
      const { data } = await api.get(`/api/message/getChatSubs?chatId=${chat}`);
      const isAdminResponse = await fetch(`/api/message/checkAdmin?&user=${userInfo.userId}&chat=${chat}`);
      const adminData = await isAdminResponse.json();
      if (adminData.error) {
        throw new Error(adminData.error);
      }
      setIsAdmin(adminData.admin);
      setSubs(data[0].users);
    } catch (error) {
      console.log(error);
    }
  };

  const handleSendMessage = async () => {
    if (textMessage.trim() || fileList.length > 0) {
      const formData = new FormData();
      formData.append('senderID', userInfo.userId);
      formData.append('type', fileList.length > 0 ? 'file' : 'text');
      formData.append('text', textMessage);
      formData.append('chatID', actualChat);

      if (fileList.length > 0) {
        formData.append('file', fileList[0].originFileObj);
        formData.append('originalFileName', fileList[0].name);
      }

      try {
        setIsFileLoading(true);
        await fetch('/api/message/send_v4', {
          method: 'POST',
          body: formData,
          headers: {
            authorization: `Bearer ${JSON.parse(localStorage.userData).token}`,
          },
        });
      } catch (error) {
        console.error(error);
      }

      setIsFileLoading(false);
      setTextMessage('');
      setFileList([]);
    }
  };

  const handleSendOnlyText = async () => {
    if (!textMessage.trim()) {
      const formData = new FormData();
      formData.append('senderID', userInfo.userId);
      formData.append('type', fileList.length > 0 ? 'file' : 'text');
      formData.append('text', textMessage);
      formData.append('chatID', actualChat);

      try {
        await fetch('/api/message/send_v4', {
          method: 'POST',
          body: formData,
          headers: {
            authorization: `Bearer ${JSON.parse(localStorage.userData).token}`,
          },
        });
        setTextMessage('');
      } catch (error) {
        console.error('Ошибка при отправке сообщения:', error);
        console.log(error);
      }
      setTextMessage('');
    }
  };

  const handleChatClick = chatId => {
    setSelectedChatId(chatId);
    fetchChatInfo(chatId, 30);
    setTextMessage('');
    dispatch(setShowUnreadIndicator(false));
  };

  const showModal = () => {
    setIsModalVisible(true);
  };

  const handleModalOk = async () => {
    setIsModalVisible(false);
    if (fileList.length > 0) {
      await handleSendMessage();
    }
  };

  const handleModalCancel = () => {
    setFileList([]);
    setIsModalVisible(false);
  };

  const handleFileChange = ({ fileList }) => {
    setFileList(fileList);
  };

  const onSearch = async value => {
    if (value.length) {
      const { data: response } = await api.get(`/api/message/find_with?text=${value}&userId=${userInfo.userId}`);

      if (response.error) {
        message.error('Соответствий не найдено!');
      } else {
        if (response.length > 0) {
          setChats(response);
          setInputValue('');
        } else {
          message.error('Ничего не найдено!');
        }
      }
    }
  };

  const handleChatDelete = async (e, chat, user) => {
    e.stopPropagation();
    await api.delete(`/api/message/delete_chat?chat=${chat}&user=${user}`);
    await fetchActualChats();
    setActualChat(null);
    setSelectedChatId(null);
    setMessages([]);
  };

  const handleMemberDelete = async (e, member, user, chat) => {
    e.stopPropagation();
    await api.delete(`/api/message/deleteMember?memberId=${member.id}&user=${user}&chat=${chat}`);
    const newSubs = subs.filter(sub => sub.id !== member.id);
    setSubs(newSubs);
    notificationApi.info({
      message: 'Удаление участника',
      description: `Участник ${member.name || member.email} удален из чата ${chatName}`,
      placement: 'topRight',
    });
  };

  return (
    <>
      {contextHolder}
      <Layout
        className={styles.chatLayout}
        style={{ minHeight: 0, height: '77vh', width: '80vw', margin: '7vh auto', borderRadius: '20px' }}
      >
        <Sider className={styles.chatSider} width={'20vw'}>
          <Header className={styles.chatHeader}>
            <div className={styles.headerContent}>
              <Text strong className={styles.chatTitle}>
                Чаты
              </Text>
              <StartNewChatModal userInfo={userInfo} />
            </div>
          </Header>
          <Divider className={styles.divider} />
          <Header className={styles.searchHeader}>
            <div className={styles.searchContainer}>
              <Input.Search
                placeholder='Поиск чата'
                allowClear
                enterButton='Найти'
                onSearch={onSearch}
                value={inputValue}
                onChange={e => setInputValue(e.target.value)}
              />
            </div>
          </Header>
          <Divider className={styles.divider} />
          <List
            itemLayout='horizontal'
            dataSource={chats}
            size='small'
            className={styles.chatList}
            renderItem={item => (
              <List.Item
                onClick={() => handleChatClick(item.id)}
                className={`${styles.chatListItem} ${selectedChatId === item.id ? styles.chatListItemSelected : ''}`}
              >
                <List.Item.Meta
                  avatar={
                    <div className={styles.avatarContainer}>
                      <Avatar>{item.chatName ? item.chatName.charAt(0) : '?'}</Avatar>
                      {unreadMessages[item.id] > 0 && (
                        <div className={styles.unreadBadge}>
                          <span className={styles.unreadCount}>{unreadMessages[item.id]}</span>
                        </div>
                      )}
                    </div>
                  }
                  title={
                    <div className={styles.chatTitleContainer}>
                      <p className={styles.chatName}>{item.chatName ? item.chatName : '?'}</p>
                    </div>
                  }
                />
                <Popconfirm
                  title='Удаление чата'
                  description='Вы уверены что хотите удалить чат из списка?'
                  onConfirm={e => handleChatDelete(e, item.id, userInfo.userId)}
                  okText='Удалить'
                  cancelText='Отмена'
                >
                  <Button onClick={e => e.stopPropagation()} icon={<DeleteOutlined />} />
                </Popconfirm>
              </List.Item>
            )}
          />
        </Sider>
        <Layout className={styles.chatContentLayout}>
          <Header className={styles.chatContentHeader}>
            {actualChat !== null && (
              <div className={styles.chatHeaderContent}>
                <Text strong className={styles.chatNameTitle}>
                  {chatName}
                </Text>
                <div>
                  {isFileLoading && <Spin className={styles.spin} />}
                  <Button className={styles.participantsButton} onClick={() => fetchSubs(actualChat)}>
                    Список участников
                  </Button>
                </div>
              </div>
            )}
          </Header>
          <Divider className={styles.contentDivider} />
          <Content className={styles.chatContent}>
            <div className={styles.messageContainer}>
              {fullArrayMessages === false && actualChat && (
                <div className={styles.loadMoreContainer}>
                  <Button className={styles.loadMoreButton} onClick={() => fetchMoreMessages(actualChat)}>
                    Загрузить сообщения выше
                  </Button>
                </div>
              )}
              <List
                itemLayout='vertical'
                dataSource={messages}
                renderItem={item => {
                  const isUserMessage = `${item.senderID}` === `${userInfo.userId}`;
                  return (
                    <List.Item className={styles.messageItem}>
                      <List.Item.Meta
                        description={
                          <div className={isUserMessage ? styles.userMessageContainer : styles.otherMessageContainer}>
                            <Text className={styles.messageSender}>{getSenderName(item, chatParticipants)}:</Text>
                            <br />
                            {item.type === 'file' ? (
                              <a href={`/api/message/file/downloadFile?name=${item.url}`}>
                                <Button icon={<FileOutlined className={styles.fileIcon} />} type='link'>
                                  <p className={styles.fileName}>
                                    {item.url ? item.url.slice(item.url.indexOf('-') + 1) : 'file'}
                                  </p>
                                </Button>
                              </a>
                            ) : (
                              <Text
                                className={`${styles.messageContent} ${
                                  isUserMessage ? styles.userMessage : styles.otherMessage
                                }`}
                              >
                                {item.text}
                              </Text>
                            )}
                            <br />
                            <Text className={styles.messageTime}>
                              {moment(item.updatedAt).locale('ru').format('DD.MM.YYYY HH:mm')}
                            </Text>
                          </div>
                        }
                      />
                    </List.Item>
                  );
                }}
              />
              <div ref={messagesEndRef} />
            </div>
            {actualChat !== null && (
              <div className={styles.messageInputContainer}>
                <Button className={styles.attachButton} icon={<PaperClipOutlined />} onClick={showModal} />
                <TextArea
                  rows={1}
                  className={styles.messageTextArea}
                  placeholder='Сообщение...'
                  value={textMessage}
                  onChange={e => setTextMessage(e.target.value)}
                  onPressEnter={handleSendOnlyText}
                />
                <Button
                  type='primary'
                  icon={<SendOutlined />}
                  className={styles.sendButton}
                  onClick={handleSendOnlyText}
                />
              </div>
            )}
          </Content>
        </Layout>
        <Modal
          title='Прикрепить файл'
          open={isModalVisible}
          onOk={handleModalOk}
          onCancel={handleModalCancel}
          okText='Прикрепить'
          cancelText='Отмена'
        >
          <Upload
            beforeUpload={() => false}
            maxCount={1}
            onChange={handleFileChange}
            fileList={fileList}
            onRemove={() => setFileList([])}
          >
            <Button icon={<PaperClipOutlined />}>Выбрать файл</Button>
          </Upload>
        </Modal>
        <Modal
          title={`Участники беседы ${chatName}:`}
          open={participantModalVisible}
          onOk={() => setParticipantModalVisible(false)}
          onCancel={() => setParticipantModalVisible(false)}
          footer={null}
          className={styles.participantModal}
        >
          <div className={styles.participantList}>
            {subs &&
              subs.map(item => (
                <div key={item.email} className={styles.participantItem}>
                  {isAdmin && (
                    <Popconfirm
                      title='Удаление участника'
                      description='Вы уверены что хотите удалить участника из чата?'
                      onConfirm={e => handleMemberDelete(e, item, userInfo.userId, actualChat)}
                      okText='Удалить'
                      cancelText='Отмена'
                    >
                      <Button
                        disabled={userInfo.name === item.name}
                        className={styles.deleteParticipantButton}
                        onClick={e => e.stopPropagation()}
                        icon={<DeleteOutlined />}
                      />
                    </Popconfirm>
                  )}
                  <p className={styles.participantName}>{item.name || item.email}</p>
                </div>
              ))}
          </div>
        </Modal>
      </Layout>
    </>
  );
};

export default ChatComponent;
